import { useEffect, useState, type ReactNode } from 'react';
import { Callout, SelectField } from '@humanforce/mango-ui-react';
import { useLocation, useNavigate } from 'react-router';
import { listOrganisations } from '../features/organisation/organisationApi';

export interface BusinessRecordScope {
  businessRecordId: string;
  businessRecordName: string;
  regionCode: string;
  inactive: boolean;
}

export const BusinessRecordScopedPage = ({ children }: { children: (scope: BusinessRecordScope) => ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryId = new URLSearchParams(location.search).get('businessRecordId') ?? '';
  const [options, setOptions] = useState<Array<BusinessRecordScope & { label: string; value: string }>>([]);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void listOrganisations().then(({ orgIds, orgs }) => {
      const values = orgIds.flatMap((id) => orgs[id].businessRecords.flatMap((record) => record.businessRecordId ? [{
        value: record.businessRecordId,
        label: record.legal.legalName,
        businessRecordId: record.businessRecordId,
        businessRecordName: record.name,
        regionCode: orgs[id].overview.identity.country,
        inactive: record.status === 'inactive',
      }] : []));
      setOptions(values);
    }).catch(() => setError('Business Records could not be loaded.'))
      .finally(() => setLoaded(true));
  }, []);

  const businessRecordId = options.some(({ value }) => value === queryId) ? queryId : options[0]?.value || '';

  useEffect(() => {
    if (!businessRecordId || queryId === businessRecordId) return;
    const params = new URLSearchParams(location.search);
    params.set('businessRecordId', businessRecordId);
    navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true });
  }, [businessRecordId, location.hash, location.pathname, location.search, navigate, queryId]);

  const selectBusinessRecord = (value: unknown) => {
    if (typeof value !== 'string' || value === businessRecordId) return;
    const params = new URLSearchParams(location.search);
    params.set('businessRecordId', value);
    navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash });
  };

  const selected = options.find((option) => option.value === businessRecordId);
  return <div className="mgo-stack mgo-gap-l">
    {options.length > 0 && <SelectField label="Business Record" required searchable value={businessRecordId} options={options} onChange={selectBusinessRecord} />}
    {error && <Callout tone="critical">{error}</Callout>}
    {!error && loaded && options.length === 0 && <Callout tone="neutral" title="No Business Records">Create a Business Record before configuring this area.</Callout>}
    {selected && children(selected)}
  </div>;
};
